/*
 *  This file hrone-tooltip.component.ts is part of HROne. The intellectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2024
 *  (C) 2015-2024. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { ChangeDetectionStrategy, Component, TemplateRef, ViewEncapsulation, computed, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

const SIZE_CLASS_MAP: Record<'small' | 'medium' | 'large', string> = {
  small: 'hrone-tooltip-sm',
  medium: 'hrone-tooltip-md',
  large: 'hrone-tooltip-lg',
};

@Component({
  selector: 'app-hrone-tooltip',
  imports: [NgTemplateOutlet],
  templateUrl: './hrone-tooltip.component.html',
  styleUrls: ['./hrone-tooltip.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HroneTooltipComponent {
  readonly content = signal('');
  readonly position = signal<'top' | 'bottom' | 'left' | 'right'>('top');
  readonly styleClass = signal('');
  readonly zIndex = signal(9999);
  readonly tooltipId = signal('');
  readonly visible = signal(false);
  readonly size = signal<'small' | 'medium' | 'large'>('small');
  readonly template = signal<TemplateRef<unknown> | null>(null);

  /**
   * Px shift (along the axis perpendicular to the arrow's point) applied to
   * re-center the arrow on the host element when viewport clamping has
   * moved the tooltip box away from its host-centered position. See
   * HroneTooltipDirective.positionTooltip().
   */
  readonly arrowOffset = signal(0);

  readonly hostClass = computed(() => {
    const base = 'hrone-tooltip';
    const sizeClass = ' ' + SIZE_CLASS_MAP[this.size()];
    const visibleClass = this.visible() ? ' hrone-tooltip--visible' : '';
    const custom = this.styleClass() ? ' ' + this.styleClass() : '';
    return base + sizeClass + visibleClass + custom;
  });

  /** Inline override for the arrow's centering axis — null lets the default CSS `50%` apply. */
  readonly arrowStyle = computed(() => {
    const offset = this.arrowOffset();
    const value = offset ? `calc(50% + ${offset}px)` : null;
    const isHorizontal = this.position() === 'top' || this.position() === 'bottom';
    return {
      left: isHorizontal ? value : null,
      top: isHorizontal ? null : value,
    };
  });
}
